import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import toast from "react-hot-toast";
import type { Rsp } from "@repo/types";
import { AuthAPI } from "@repo/api/auth";
import { UserAPI } from "@repo/api/user";
import { Button, Spinner } from "@heroui/react";
import { asSearchString } from "@repo/utils/search-params";
import { PhoneInput } from "@/components/from/phone-input";
import { recoveryCountryCodes } from "@/data/country-codes";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import ErrorPage from "@/pages/-error";
import { F2A_Biz } from "@/pages/f2a/-const";

export type RecoveryPhoneSearch = {
  recovery_ticket?: string;
  session_code?: string;
};

export const Route = createFileRoute("/user/account/recovery_phone")({
  validateSearch: (search: Record<string, unknown>): RecoveryPhoneSearch => ({
    recovery_ticket: asSearchString(search.recovery_ticket),
    session_code: asSearchString(search.session_code),
  }),
  component: RecoveryPhone,
});

const routeApi = getRouteApi("/user/account/recovery_phone");

export default function RecoveryPhone() {
  const navigate = useNavigate();
  const { recovery_ticket: recoveryTicket, session_code: sessionCode } = routeApi.useSearch();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [countryCode, setCountryCode] = useState("86");
  const [loading, setLoading] = useState(false);

  useDocumentTitle("绑定新号码");

  const handleGetSmsCode = () => {
    if (!phoneNumber) {
      toast.error("请输入手机号");
      return Promise.reject();
    }
    if (!countryCode) {
      toast.error("请选择国家区号");
      return Promise.reject();
    }
    return AuthAPI.getSMSCode(phoneNumber, countryCode, F2A_Biz.ACCOUNT_RECOVERY).then(
      (res) => {
        if (res.status !== 0) {
          toast.error(res.msg || "验证码发送失败");
          return Promise.reject();
        }
        toast.success("验证码发送成功");
      },
      (err: Rsp<null>) => {
        toast.error(err.msg || "验证码发送失败");
        return Promise.reject();
      },
    );
  };

  const handleSubmit = () => {
    if (!recoveryTicket || !sessionCode) {
      toast.error("授权失效，请重新开始");
      return;
    }
    if (!phoneNumber) {
      toast.error("请输入手机号");
      return;
    }
    if (!smsCode) {
      toast.error("请输入验证码");
      return;
    }

    setLoading(true);
    UserAPI.completePhoneRecovery({
      recovery_ticket: recoveryTicket,
      session_code: sessionCode,
      country_code: countryCode,
      phone_number: phoneNumber,
      code: smsCode,
    })
      .then(
        () => {
          navigate({
            to: "/result/success",
            search: {
              type: "success",
              title: "手机号更新成功",
              end: "true",
              op: "返回登录",
              action: "close_webview",
            },
            replace: true,
          });
        },
        (err: Rsp<null>) => {
          toast.error(err.msg);
        },
      )
      .finally(() => setLoading(false));
  };

  if (!recoveryTicket || !sessionCode) {
    return <ErrorPage error={{ msg: "参数无效", request_id: "FURRIES.PARAMS.INVALID" }} />;
  }

  return (
    <div className="p-4 max-w-md mx-auto">
      <div className="text-3xl font-bold text-gray-800 mb-1 mt-4">绑定新手机号</div>
      <div className="text-md text-gray-500 mb-6">输入新手机号和验证码，完成更换</div>
      <form className="flex flex-col gap-6" onSubmit={(e) => e.preventDefault()}>
        <PhoneInput
          value={phoneNumber}
          onChange={setPhoneNumber}
          onGetCode={handleGetSmsCode}
          codeValue={smsCode}
          onCodeChange={setSmsCode}
          countryCode={countryCode}
          onCountryCodeChange={setCountryCode}
          countries={recoveryCountryCodes}
          disabled={loading}
        />
        <Button
          className="w-full rounded-full"
          type="button"
          isPending={loading}
          isDisabled={!phoneNumber || !smsCode || loading}
          onPress={handleSubmit}
        >
          {({ isPending }) => (
            <>
              {isPending && <Spinner size="sm" color="current" />}
              完成
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
